import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format, addWeeks, subWeeks, startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth, isSameDay, isToday, addMonths, subMonths } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, Plus, Clock, Calendar as CalendarIcon, Moon, Sun, Wind, Cloud, Music, Feather, Sparkles, ChevronDown, Loader2 } from 'lucide-react';
import { Event } from '../../services/schedule/types';
import { scheduleService } from '../../services/schedule/schedule';
import { useAuth } from '../../hooks/useAuth';
import { toast } from 'sonner';
import EventModal from './EventModal';

interface MontblancCalendarProps {
  onAddEvent?: (date: Date) => void;
}

export const MontblancCalendar: React.FC<MontblancCalendarProps> = ({ onAddEvent }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [hoveredDate, setHoveredDate] = useState<Date | null>(null);
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [events, setEvents] = useState<Event[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { user, isLoading: isAuthLoading } = useAuth();

  const weekStart = startOfWeek(currentDate, { locale: ptBR });
  const weekEnd = endOfWeek(currentDate, { locale: ptBR });
  const daysInWeek = eachDayOfInterval({ start: weekStart, end: weekEnd });

  useEffect(() => {
    if (user && !isAuthLoading) {
      loadEvents();
    }
  }, [user, isAuthLoading]);

  const loadEvents = async () => {
    if (!user) return;
    
    try {
      setIsLoading(true);
      const loadedEvents = await scheduleService.getEvents(user.id);
      setEvents(loadedEvents);
    } catch (error) {
      console.error('Erro ao carregar eventos:', error);
      toast.error('Não foi possível carregar os eventos');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddEvent = async (date: Date) => {
    if (!user) {
      toast.error('Você precisa estar autenticado para adicionar eventos');
      return;
    }
    setSelectedDate(date);
    setSelectedEvent(null);
    setIsModalOpen(true);
  };

  const handleEditEvent = (event: Event) => {
    setSelectedEvent(event);
    setSelectedDate(new Date(event.startTime));
    setIsModalOpen(true);
  };

  const handleSaveEvent = async (eventData: Partial<Event>) => {
    if (!user) return;

    try {
      setIsLoading(true);
      if (selectedEvent?.id) {
        // Update existing event
        await scheduleService.updateEvent({
          ...eventData,
          id: selectedEvent.id,
          userId: user.id
        });
        toast.success('Evento atualizado com sucesso!');
      } else {
        // Create new event
        await scheduleService.createEvent({
          ...eventData,
          userId: user.id
        } as Omit<Event, 'id'>);
        toast.success('Evento criado com sucesso!');
      }
      await loadEvents();
    } catch (error: any) {
      console.error('Erro ao salvar evento:', error);
      toast.error(error.message || 'Erro ao salvar evento');
    } finally {
      setIsLoading(false);
      setIsModalOpen(false);
    }
  };

  const handleDeleteEvent = async (eventId: string) => {
    if (!user) return;

    try {
      setIsLoading(true);
      await scheduleService.deleteEvent(eventId);
      toast.success('Evento excluído com sucesso!');
      await loadEvents();
    } catch (error: any) {
      console.error('Erro ao excluir evento:', error);
      toast.error(error.message || 'Erro ao excluir evento');
    } finally {
      setIsLoading(false);
    }
  };

  if (isAuthLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin text-[rgb(0,122,255)]" />
      </div>
    );
  }

  const getCategoryStyle = (category: Event['category']) => {
    const styles = {
      work: 'bg-[rgb(88,86,214,0.2)] border-[rgb(88,86,214,0.4)] text-[rgb(88,86,214)]',
      health: 'bg-[rgb(52,199,89,0.2)] border-[rgb(52,199,89,0.4)] text-[rgb(52,199,89)]',
      personal: 'bg-[rgb(0,122,255,0.2)] border-[rgb(0,122,255,0.4)] text-[rgb(0,122,255)]',
      inspiration: 'bg-[rgb(255,149,0,0.2)] border-[rgb(255,149,0,0.4)] text-[rgb(255,149,0)]',
      reflection: 'bg-[rgb(88,86,214,0.2)] border-[rgb(88,86,214,0.4)] text-[rgb(88,86,214)]'
    };
    return styles[category];
  };

  const getMoodIcon = (mood?: Event['mood']) => {
    switch (mood) {
      case 'morning':
        return <Sun className="w-4 h-4 text-amber-400" />;
      case 'evening':
        return <Moon className="w-4 h-4 text-purple-400" />;
      case 'inspiration':
        return <Sparkles className="w-4 h-4 text-blue-400" />;
      default:
        return null;
    }
  };

  const eventsForDate = (date: Date) => 
    events.filter(event => isSameDay(new Date(event.startTime), date));

  return (
    <div className="w-full">
      <div className="w-full bg-[rgb(28,28,30)] rounded-2xl p-6 space-y-6">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="relative overflow-hidden rounded-2xl bg-[rgb(44,44,46)] border border-[rgb(60,60,62)]"
        >
          {isLoading ? (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="w-6 h-6 animate-spin text-[rgb(0,122,255)]" />
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="p-6 border-b border-[rgb(60,60,62)]">
                <div className="flex items-center justify-between mb-6">
                  {/* Mês e Ano com Dropdown */}
                  <div className="relative">
                    <motion.button
                      className="text-[28px] font-semibold text-white flex items-center gap-2 hover:text-white/90 transition-all duration-200"
                      onClick={() => setShowMonthPicker(!showMonthPicker)}
                    >
                      {format(currentDate, 'MMMM yyyy', { locale: ptBR })}
                      <ChevronDown className="w-5 h-5" />
                    </motion.button>

                    <AnimatePresence>
                      {showMonthPicker && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className="absolute top-full left-0 mt-2 bg-[rgb(44,44,46)] border border-[rgb(60,60,62)] rounded-2xl p-2 z-10"
                        >
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => {
                                setCurrentDate(subMonths(currentDate, 1));
                                setShowMonthPicker(false);
                              }}
                              className="p-2 hover:bg-[rgb(60,60,62)] rounded-2xl transition-all duration-200"
                            >
                              <ChevronLeft className="w-4 h-4 text-[rgb(152,152,157)]" />
                            </button>
                            <button
                              onClick={() => {
                                setCurrentDate(new Date());
                                setShowMonthPicker(false);
                              }}
                              className="px-3 py-2 text-sm font-medium hover:bg-[rgb(60,60,62)] rounded-2xl transition-all duration-200 text-[rgb(152,152,157)]"
                            >
                              Hoje
                            </button>
                            <button
                              onClick={() => {
                                setCurrentDate(addMonths(currentDate, 1));
                                setShowMonthPicker(false);
                              }}
                              className="p-2 hover:bg-[rgb(60,60,62)] rounded-2xl transition-all duration-200"
                            >
                              <ChevronRight className="w-4 h-4 text-[rgb(152,152,157)]" />
                            </button>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Botões de Navegação */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentDate(subWeeks(currentDate, 1))}
                      className="bg-[rgb(44,44,46)] hover:bg-[rgb(60,60,62)] text-[rgb(152,152,157)] rounded-2xl p-2 transition-all duration-200"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setCurrentDate(new Date())}
                      className="bg-[rgb(44,44,46)] hover:bg-[rgb(60,60,62)] text-[rgb(152,152,157)] rounded-2xl py-2 px-3 text-sm font-medium transition-all duration-200"
                    >
                      Hoje
                    </button>
                    <button
                      onClick={() => setCurrentDate(addWeeks(currentDate, 1))}
                      className="bg-[rgb(44,44,46)] hover:bg-[rgb(60,60,62)] text-[rgb(152,152,157)] rounded-2xl p-2 transition-all duration-200"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleAddEvent(selectedDate)}
                      className="bg-white hover:bg-white/90 text-black rounded-2xl py-3 px-4 text-sm font-medium transition-all duration-200 flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Novo Evento
                    </button>
                  </div>
                </div>

                {/* Dias da Semana */}
                <div className="grid grid-cols-7 gap-4 mt-6">
                  {daysInWeek.map((date, i) => (
                    <div
                      key={i}
                      className="text-center"
                    >
                      <div className="text-sm font-medium text-[rgb(152,152,157)]">
                        {format(date, 'EEE', { locale: ptBR })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Grid de Dias */}
              <div className="p-6">
                <div className="grid grid-cols-7 gap-4">
                  {daysInWeek.map((date, i) => {
                    const dayEvents = eventsForDate(date);
                    const isSelected = isSameDay(date, selectedDate);
                    const isHovered = hoveredDate && isSameDay(date, hoveredDate);
                    
                    return (
                      <motion.div
                        key={i}
                        className={`
                          relative p-2 rounded-2xl cursor-pointer transition-all duration-200
                          ${isToday(date) ? 'bg-white text-black' : 'hover:bg-[rgb(60,60,62)]'}
                          ${isSelected ? 'bg-[rgb(44,44,46)] text-white' : 'text-[rgb(152,152,157)]'}
                        `}
                        onHoverStart={() => setHoveredDate(date)}
                        onHoverEnd={() => setHoveredDate(null)}
                        onClick={() => handleAddEvent(date)}
                      >
                        <div className="text-[20px] font-semibold">
                          {format(date, 'd')}
                        </div>

                        {/* Lista de Eventos */}
                        <div className="mt-2 space-y-1">
                          {dayEvents.map((event) => (
                            <motion.button
                              key={event.id}
                              className={`
                                w-full text-left p-2 rounded-2xl border text-sm
                                transition-all duration-200 hover:scale-[1.02]
                                ${getCategoryStyle(event.category)}
                              `}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditEvent(event);
                              }}
                            >
                              <div className="flex items-center gap-2">
                                {getMoodIcon(event.mood)}
                                <span className="font-medium truncate">{event.title}</span>
                              </div>
                              <div className="flex items-center gap-1 mt-1 opacity-80 text-xs">
                                <Clock className="w-3 h-3" />
                                <span>{format(new Date(event.startTime), 'HH:mm')}</span>
                              </div>
                            </motion.button>
                          ))}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </motion.div>
      </div>

      {/* Modal de Evento */}
      <EventModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveEvent}
        onDelete={selectedEvent ? handleDeleteEvent : undefined}
        event={selectedEvent}
        selectedDate={selectedDate}
      />
    </div>
  );
};
